import { WebViewerInstance } from "@pdftron/webviewer";
import { Core } from "@pdftron/webviewer/types";

export type SourceLocation = {
  pageNumber: number;
  bbox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
};

type DrawOptions = {
  /**
   * When true, jump the viewer to the first drawn annotation. Set to
   * false to suppress the jump
   * @default true
   */
  jumpToFirst?: boolean;
  /**
   * When true, clicking an annotation will smoothly scroll the viewer to it.
   * If false, the viewer will jump to the annotation without a scroll animation.
   * @default false
   */
  smoothScroll?: boolean;
  /**
   * Fired after the viewer has actually jumped to the first annotation
   * (i.e. after the `annotationsDrawn` event). Not fired when `jumpToFirst`
   * is false or when no bboxed annotation was drawn.
   */
  onJumped?: () => void;
};

/**
 * Draw a blue rectangle annotation on the PDF for each location with a bbox,
 * and jump the viewer to the first one. Locations without a bbox fall back to
 * `setCurrentPage` for the first such location. Returns the annotations added
 * during this call, sorted by page, so callers can wire Next/Prev controls.
 */
export function drawSourceAnnotations(
  instance: WebViewerInstance,
  locations: SourceLocation[],
  options: DrawOptions = {},
): Core.Annotations.RectangleAnnotation[] {
  const { jumpToFirst = true, smoothScroll = false, onJumped } = options;
  const { documentViewer, annotationManager, Annotations } = instance.Core;

  annotationManager.deleteAnnotations(annotationManager.getAnnotationsList(), {
    force: true,
  });

  const created: Core.Annotations.RectangleAnnotation[] = [];
  let firstPageOnly: number | undefined;
  let hasRegisteredJumper = false;

  for (let i = 0; i < locations.length; i++) {
    const { pageNumber, bbox } = locations[i];

    if (!bbox) {
      if (firstPageOnly === undefined) firstPageOnly = pageNumber;
      continue;
    }

    const annotation = new Annotations.RectangleAnnotation({
      PageNumber: pageNumber,
      X: bbox.x - 10,
      Y: bbox.y - 5,
      Width: bbox.width + 20,
      Height: bbox.height + 10,
      StrokeColor: new Annotations.Color(0, 96, 255, 0.8),
      StrokeThickness: 2,
    });
    annotation.ReadOnly = true;

    // Scroll to the first annotation with a bbox when it's drawn.
    // Register the jumper BEFORE adding the first annotation so the
    // `annotationsDrawn` event we're about to trigger actually wakes us up.
    // If we register after the draw, the event has already fired and the
    // viewer never scrolls — the boxes end up drawn on an off-screen page.
    if (jumpToFirst && !hasRegisteredJumper) {
      const target = annotation;
      const jumper = () => {
        annotationManager.jumpToAnnotation(target, {
          isSmoothScroll: smoothScroll,
          zoom: 1, // Should be default but bug in Apryse means without it causes the viewer to remove anything outside a tiny slide of the viewport
        });
        onJumped?.();
        annotationManager.removeEventListener("annotationsDrawn", jumper);
      };
      annotationManager.addEventListener("annotationsDrawn", jumper);
      hasRegisteredJumper = true;
    }

    annotationManager.addAnnotation(annotation);
    annotationManager.redrawAnnotation(annotation);
    created.push(annotation);
  }

  if (jumpToFirst && !hasRegisteredJumper && firstPageOnly !== undefined) {
    documentViewer.setCurrentPage(firstPageOnly, smoothScroll);
  }

  return created.sort((a, b) => a.PageNumber - b.PageNumber);
}
